import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormField } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { InternalUserPicker } from "@/components/users/InternalUserPicker";
import { collectionPackagesApi } from "@/api/collectionPackages";

interface PackageAssigneesEditorProps {
  /** CollectionPackage whose many-to-many assignees are being edited. */
  packageId: string;
  /**
   * Called after each successful add / remove so callers can refresh their
   * own surfaces (the staging-detail context strip, the queue list row).
   * The editor always invalidates its own ["package-assignees", id] query.
   */
  onChanged?: () => void;
  disabled?: boolean;
}

/**
 * External Portal v2 (PROB-G) — shared Manage-Assignees editor. The
 * many-to-many assignee set for a CollectionPackage is rendered as a live
 * editor: each add / remove fires the controller endpoint immediately
 * (no Save button). Extracted from <StagedSubmissionReviewPanel> so the
 * same control backs both the staging-detail panel and the queue-row
 * quick-action modal without duplicating the add/remove diff logic.
 */
export function PackageAssigneesEditor({
  packageId,
  onChanged,
  disabled,
}: PackageAssigneesEditorProps) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const assignees = useQuery({
    queryKey: ["package-assignees", packageId],
    queryFn: () => collectionPackagesApi.listAssignees(packageId),
    enabled: Boolean(packageId),
  });
  const assigneeIds = useMemo(
    () => (assignees.data ?? []).map((a) => a.userId),
    [assignees.data],
  );

  function refresh() {
    void qc.invalidateQueries({ queryKey: ["package-assignees", packageId] });
    onChanged?.();
  }

  const addMut = useMutation({
    mutationFn: (userId: string) =>
      collectionPackagesApi.addAssignee(packageId, userId),
    onSuccess: refresh,
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });
  const removeMut = useMutation({
    mutationFn: (userId: string) =>
      collectionPackagesApi.removeAssignee(packageId, userId),
    onSuccess: refresh,
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // Diff the picker's new selection against the server's current set and
  // fire the matching add / remove. Immediate writes — no Save button.
  function handleChange(next: string[]) {
    for (const uid of next) {
      if (!assigneeIds.includes(uid)) addMut.mutate(uid);
    }
    for (const uid of assigneeIds) {
      if (!next.includes(uid)) removeMut.mutate(uid);
    }
  }

  return (
    <div className="flex flex-col gap-space-2">
      <FormField label="Assignees">
        <InternalUserPicker
          value={assigneeIds}
          onChange={handleChange}
          disabled={
            disabled ||
            assignees.isLoading ||
            addMut.isPending ||
            removeMut.isPending
          }
          placeholder="Search by name, email, or role code…"
        />
      </FormField>
      {!assignees.isLoading && assigneeIds.length === 0 && (
        <p className="text-caption text-gray-mid italic">No one assigned yet.</p>
      )}
    </div>
  );
}
